import React, { useCallback, useMemo } from 'react';
import styled from 'styled-components';
import { GuestAvatars } from '../../../@resources/icons/i8/GuestAvatars';
import { tintSafe, useObserver } from '../../../CodesThatsGottaGoSomewhereElse';
import { CommandDispatcher } from '../../../concepts/commands/CommandDispatcher';
import { Icon } from '../../../concepts/resources/Icon';
import { KeepPopoutWidgetOpenButton } from '../../../ui/KeepOpenButton';
import { Position } from '../../../ui/Position';
import { useResolve } from '../../Container';
import { Participant } from '../../canvas/participants/Participant';
import { ParticipantManager } from '../../canvas/participants/ParticipantManager';
import { toolEquip } from '../../canvas/tooling/ToolCommands';
import { ToolManager } from '../../canvas/tooling/ToolManager';
import { ElementSelector } from '../../canvas/ui/ElementSelector';
import { Emoticon } from '../../canvas/visualization/Emoticon';
import { PopoutWidget } from '../../canvas/workspace/widgets/WidgetInterface';
import { Flexer } from '../../dashboard/elements/LayoutElements';
import { Colors } from './../../../ui';

const { Model, Item, ItemSet, ItemGroup } = ElementSelector;

const lengthToSize = {
    0: 1.25,
    1: 1.25,
    2: 1.125,
    3: 1.0,
    4: .75,
};

const TokenBorder = styled.div<{ color: string }>`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 3rem;
    height: 3rem;
    font-family: 'Rubik';    
    font-weight: 450;
    color: ${p => p.color};
    border: solid .25rem ${p => tintSafe(.4, p.color)};
    border-radius: 50%;
    box-shadow: 0 0 .0625rem .0625rem ${p => tintSafe(.4, p.color)};
    background: ${p => tintSafe(.75, p.color)};
`;

const TokenImage = (props: { src: string }) =>
    <div style={{
        width: '100%',
        height: '100%',
        borderRadius: '50%',
        background: `url(${GuestAvatars.lookup(props.src) ?? props.src})`,
        backgroundSize: 'cover'
    }} />;
const TokenText = styled.div<{ size: number }>`
    font-size: ${p => p.size}rem;
`;

const TokenUser = (props: { userId: string }) => {
    const ptpt = useResolve(ParticipantManager).get(props.userId.slice(1, -1));
    if (ptpt.photo) {
        return <TokenImage src={ptpt.photo} />;
    }
    else {
        const text = Participant.toInitials(ptpt.name);
        return <TokenText size={lengthToSize[text?.length ?? 1] ?? lengthToSize[4]}>
            {text}
        </TokenText>;
    }
};

const TokenValue = React.memo<{ value: string }>(({ value }) => {
    if (value === '(ME)' || value.match(/^\((FU|GU|US)\w{8,12}\)/i)) {
        return <TokenUser userId={value} />;
    }
    const emote = Emoticon.lookup(value);
    if (!!emote) {
        return <Icon size="3" src={`deqo://icons/tokens/${emote}`} />;
    }
    return <TokenText size={lengthToSize[value?.length ?? 1] ?? lengthToSize[4]}>
        {value}
    </TokenText>;
});

const TokenPreview = ElementSelector.ItemPreview(
    (props: any) => <TokenBorder color={Colors.normalize(props.color) ?? Colors.primary}>
        <TokenValue value={props.value} />
    </TokenBorder>
);

const TokenItem = (value: string, color: string) => {
    const label = value;
    value = Emoticon.rlookup(value) ?? value;
    return Item(value || `blank:${color}`, label, { value, color }, TokenPreview);
};

const EmoteItem = (value: string, label: string, color: string) => {
    return Item(value, label, { value, color }, TokenPreview);
};

const blank = ItemSet('blank', 'Blank', 6, [
    TokenItem('', '#9c27b0'),
    TokenItem('', '#673ab7'),
    TokenItem('', '#3f51b5'),
    TokenItem('', '#2196f3'),
    TokenItem('', '#03a9f4'),
    TokenItem('', '#00bcd4'),
    TokenItem('', '#009688'),
    TokenItem('', '#4caf50'),
    TokenItem('', '#8bc34a'),
    TokenItem('', '#cddc39'),
    TokenItem('', '#ffeb3b'),
    TokenItem('', '#ffc107'),
    TokenItem('', '#ff9800'),
    TokenItem('', '#ff5722'),
    TokenItem('', '#f44336'),
    TokenItem('', '#e91e63'),
    TokenItem('', '#795548'),
    TokenItem('', '#607d8b'),
]);

const emotes = ItemSet('emotes', 'Emotes', 6, [
    EmoteItem(':)', 'classic-smiley', '#8bc34a'),
    EmoteItem(':!', 'cute-derp', '#8bc34a'),
    EmoteItem('|:)', 'classic-smiley-eyebrows', '#8bc34a'),

    EmoteItem(':(', 'cute-sad-face', '#ff5722'),
    EmoteItem(':C', 'classic-sad-face', '#ff5722'),
    EmoteItem(':_(', 'sad-face-tear', '#ff5722'),

    EmoteItem(':D', 'wide-smile', '#4caf50'),
    EmoteItem('xD', 'wide-smile-closed-eyes-1', '#4caf50'),
    EmoteItem('3D', 'wide-smile-closed-eyes-2', '#4caf50'),

    EmoteItem(':/', 'thinking', '#ff9800'),
    EmoteItem('xP', 'closed-eyes-derp', '#ff9800'),
    EmoteItem(':3', 'dissapointed-face', '#ff9800'),

    EmoteItem('8)', 'cool-steve', '#009688'),
    EmoteItem('8D', 'cool-steve-wide-smile', '#009688'),
    EmoteItem('<3D', 'wide-smile-heart-eyes', '#009688'),

    EmoteItem('>:(', 'angry-face', '#f44336'),
    EmoteItem('Dx', 'sad-face-closed-eyes-1', '#f44336'),
    EmoteItem('D:', 'sad-face-closed-eyes-2', '#f44336'),

    EmoteItem(':_D', 'laugh-cry', '#009688'),
    EmoteItem('o:)', 'angel', '#ff9800'),
    EmoteItem('>:)', 'devil', '#673ab7'),

    EmoteItem(':O', 'surprised-face', '#9c27b0'),
    EmoteItem(':|', 'straight-mouth', '#9c27b0'),
    EmoteItem('=|', 'straight-eyes-mouth', '#9c27b0'),

    EmoteItem(':*', 'kiss-face-heart', '#2196f3'),
    EmoteItem(':x', 'kiss-face', '#2196f3'),
    EmoteItem(':&', 'tongue-out', '#2196f3'),

    EmoteItem(':P', 'derp', '#3f51b5'),
    EmoteItem('%O', 'dizzy-eyes', '#3f51b5'),
    EmoteItem(':@', 'sick-face', '#4caf50'),

    EmoteItem(':E', 'grimace', '#673ab7'),
    EmoteItem('<:)', 'party', '#673ab7'),
    EmoteItem(':#', 'zipper-mouth', '#673ab7'),

    EmoteItem(':L', 'thinking-hand', '#607d8b'),
    EmoteItem(':S', 'confused', '#607d8b'),
    EmoteItem(';|', 'sarcastic-face', '#607d8b'),

    EmoteItem('8|', 'monocle', '#607d8b'),
    EmoteItem('(Y)', 'thumbs-up', '#4caf50'),
    EmoteItem('(N)', 'thumbs-down', '#f44336'),

    EmoteItem('(B)', 'beer', '#ff9800'),
    EmoteItem('(C)', 'coffee-cup', '#795548'),
    EmoteItem('(K)', 'lips', '#e91e63'),

    EmoteItem('<3', 'heart', '#e91e63'),
    EmoteItem('</3', 'broken-heart', '#e91e63'),
    EmoteItem('(I)', 'lightbulb', '#2196f3'),
    EmoteItem('(G)', 'present', '#2196f3'),
]);

const numbers = ItemSet('numbers', 'Numbers', 6, [
    TokenItem('1', '#ffc107'),
    TokenItem('2', '#cddc39'),
    TokenItem('3', '#8bc34a'),
    TokenItem('4', '#4caf50'),
    TokenItem('5', '#009688'),
    TokenItem('6', '#00bcd4'),
    TokenItem('7', '#03a9f4'),
    TokenItem('8', '#2196f3'),
    TokenItem('9', '#3f51b5'),
    TokenItem('10', '#673ab7'),
    TokenItem('11', '#9c27b0'),
    TokenItem('12', '#e91e63'),
]);

const storyPoints = ItemSet('storyPoints', 'Story Points', 6, [
    TokenItem('0', '#cddc39'),
    TokenItem('1', '#8bc34a'),
    TokenItem('2', '#4caf50'),
    TokenItem('3', '#009688'),
    TokenItem('5', '#00bcd4'),
    TokenItem('8', '#03a9f4'),
    TokenItem('13', '#2196f3'),
    TokenItem('20', '#3f51b5'),
    TokenItem('40', '#673ab7'),
    TokenItem('100', '#9c27b0'),
    TokenItem('(C)', '#795548'),
    TokenItem('?', '#f44336'),
]);

const tshirts = ItemSet('tshirts', 'T-Shirt Sizing', 6, [
    TokenItem('XS', '#4caf50'),
    TokenItem('S', '#8bc34a'),
    TokenItem('M', '#cddc39'),
    TokenItem('L', '#ff9800'),
    TokenItem('XL', '#f44336'),
]);

const letters = ItemSet('letters', 'Letters', 6, [
    TokenItem('WHO', '#3f51b5'),
    TokenItem('WHAT', '#2196f3'),
    TokenItem('WHEN', '#009688'),

    TokenItem('YES', '#4caf50'),
    TokenItem('NO', '#f44336'),
    TokenItem('LOL', '#673ab7'),

    TokenItem('WHY', '#4caf50'),
    TokenItem('HOW', '#ff9800'),
    TokenItem('WOW', '#f44336'),

    TokenItem('YUP', '#4caf50'),
    TokenItem('NOPE', '#f44336'),
    TokenItem('IDK', '#673ab7'),

    TokenItem('IF', '#9c27b0'),
    TokenItem('THEN', '#9c27b0'),
    TokenItem('ELSE', '#9c27b0'),

    TokenItem('DO', '#4caf50'),
    TokenItem('DONT', '#f44336'),
    TokenItem('WTF', '#673ab7'),

    TokenItem('?', '#4caf50'),
    TokenItem('??', '#2196f3'),
    TokenItem('???', '#9c27b0'),

    TokenItem('!', '#4caf50'),
    TokenItem('!!', '#2196f3'),
    TokenItem('!!!', '#9c27b0'),

    TokenItem('-', '#03a9f4'),
    TokenItem('*', '#03a9f4'),
    TokenItem('/', '#03a9f4'),
    TokenItem('=', '#03a9f4'),
]);

const model = Model(
    ItemGroup('blank', 'Blank', 'deqo://icons/d2/token-blank@[0.8,0.8,0]', [
        blank,
    ]),
    ItemGroup('emotes', 'Emotes', 'deqo://icons/tokens/wide-smile@[1.25,1.25,0]', [
        emotes,
    ]),
    ItemGroup('numbers', 'Numbers', 'deqo://icons/i8/123', [
        numbers,
        storyPoints,
        tshirts,
    ]),
    ItemGroup('letters', 'Letters', 'deqo://icons/i8/abc', [
        letters,
    ]),
);

export const TokenWidget = React.memo<{ widget: PopoutWidget }>(({ widget }) => {

    const tools = useResolve(ToolManager);
    const ptpts = useObserver(useResolve(ParticipantManager));
    const users = useMemo(() => (
        ItemGroup('team', 'Team', 'deqo://icons/i8/user', [
            ItemSet('team', 'Team', 6, ptpts.filter(x => x.loaded).map(
                x => Item(x.id, x.name, { value: `(${x.id})`, color: x.color }, TokenPreview)
            ))
        ])
    ), [ptpts]);

    const dispatch = useResolve(CommandDispatcher);

    const onSelect = useCallback((item: any, selectMode: 'drag' | 'click') => {
        widget.update({ open: !widget.autoClose });
        tools.setSettings('tokenTool', item.props);
        const mode = selectMode === 'click' ? 'plop' : 'drop';
        const edit = !item.props.value ? 'edit' : '';
        dispatch(toolEquip, ['tokenTool', `${mode}:${edit}`]);
    }, []);

    return <Flexer relative>
        <ElementSelector name="tokens" model={[...model, users]} onSelectItem={onSelect} />
        <Position at="topRight" inset=".6">
            <KeepPopoutWidgetOpenButton widget={widget} />
        </Position>
    </Flexer>;
});

